import axios, { AxiosError } from 'axios';
import type { AnalysisResponse, AssessmentListResponse, AssessmentStatusResponse, JobStatusResponse, ReportResponse, TokenResponse, UploadResponse } from '../types/api';

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sprint 5 client feedback 2026-06-26 — during NFT a backend
// restart left every in-flight request returning "Request failed
// with status code 502" (nginx → backend gateway error). End users
// can't read that as "service is restarting". Intercept 502/503/504
// + network-level errors (no `error.response`) and (a) rewrite
// error.message to a friendly sentence, (b) auto-retry GETs once
// after 2 s so transient restarts heal themselves before the page
// even surfaces the error. POST/PUT/DELETE are NOT auto-retried —
// the user is told to retry manually so we never double-apply a
// write. A custom code "SERVICE_UNAVAILABLE" is set on the error
// object so any callsite that wants to render its own UI (banner,
// inline pill) can branch on it.
const FRIENDLY_GATEWAY_MSG =
  'The service is temporarily unavailable. It may be restarting — please retry in a few seconds.';

const _isGatewayError = (error: AxiosError): boolean => {
  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  // No response at all → network drop / container down between connect
  // and first byte. Same user-visible cause as a 502.
  return !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
};

const _sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Sprint 5 client feedback 2026-07-07 (BE Issue #6) — capture the
 * user's intended path + a machine-readable reason before we hand off
 * to /login, so LoginPage can:
 *   1. Show a friendly banner ("Your session expired…") instead of
 *      an unexplained login screen.
 *   2. Send the user back to where they were after a successful
 *      re-login instead of dropping them at /dashboard.
 *
 * Both keys live in sessionStorage — sessionStorage clears when the
 * browser closes, and we only want the redirect-back behaviour for
 * the current tab. localStorage would leak stale return paths across
 * unrelated sessions.
 */
function _rememberAuthContext(reason: 'session_expired' | 'refresh_failed'): void {
  try {
    sessionStorage.setItem('login_reason', reason);
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    // Don't loop back to /login itself.
    if (returnTo && !returnTo.startsWith('/login')) {
      sessionStorage.setItem('login_return_to', returnTo);
    }
  } catch {
    /* noop — sessionStorage disabled (private mode / cookie block) */
  }
}

/**
 * Sprint 5 client feedback 2026-07-07 (BE Issue #6) — single-flight
 * refresh so a route change firing 5 parallel requests only triggers
 * one /auth/refresh call. All queued requests wait on the same
 * promise; on success they retry with the fresh token, on failure
 * they all fall through to the session-expired path.
 */
let _refreshInFlight: Promise<string | null> | null = null;

async function _refreshAccessToken(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight;
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return null;
  _refreshInFlight = (async () => {
    try {
      const { data } = await axios.post<{ access_token: string }>(
        '/api/v1/auth/refresh',
        { refresh_token: refresh },
      );
      localStorage.setItem('access_token', data.access_token);
      return data.access_token;
    } catch {
      return null;
    } finally {
      // Clear a tick later so parallel callers can await the same
      // promise; then next attempt starts fresh.
      setTimeout(() => { _refreshInFlight = null; }, 0);
    }
  })();
  return _refreshInFlight;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Sprint 5 VAPT 2026-06-30 (Issue 4) — server-side inactivity
      // timeout returns 401 with `error: SESSION_EXPIRED`. Don't
      // attempt a token refresh in that case (the refresh token's
      // session has also timed out); kick straight to /login with a
      // toast/banner so the user knows why.
      const data = error.response?.data as { error?: string } | undefined;
      if (data?.error === 'SESSION_EXPIRED') {
        _rememberAuthContext('session_expired');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.assign('/login');
        return Promise.reject(error);
      }
      // Single-flight refresh — deduplicates parallel refresh calls.
      const newToken = await _refreshAccessToken();
      if (newToken && error.config) {
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return api.request(error.config);
      }
      if (newToken === null) {
        _rememberAuthContext('refresh_failed');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.assign('/login');
      }
    }

    if (_isGatewayError(error)) {
      const cfg = error.config as
        | (typeof error.config & { _gatewayRetry?: boolean })
        | undefined;
      const method = (cfg?.method || 'get').toLowerCase();
      // Idempotent GETs are auto-retried once after a short pause so a
      // 5-15 s backend restart doesn't bubble up to the UI at all.
      if (cfg && !cfg._gatewayRetry && (method === 'get' || method === 'head')) {
        cfg._gatewayRetry = true;
        // M4 (2026-06-29 review): if the caller passed an AbortSignal
        // and it's already aborted (page unmounted etc.), short-circuit
        // the 2 s sleep and the retry. Otherwise we orphan the retry
        // for up to 2 s after the user navigated away.
        if (cfg.signal?.aborted) {
          // Surface the user-friendly message but skip the retry.
        } else {
          await _sleep(2000);
          if (cfg.signal?.aborted) {
            // User cancelled during the wait — don't fire a stale GET.
          } else {
            try {
              return await api.request(cfg);
            } catch (retryError) {
              // Fall through to the friendly-message branch below.
              error = retryError as AxiosError;
            }
          }
        }
      }
      error.message = FRIENDLY_GATEWAY_MSG;
      (error as AxiosError & { code?: string }).code = 'SERVICE_UNAVAILABLE';
    }

    return Promise.reject(error);
  }
);

export async function login(email: string, password: string): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>('/auth/login', { email, password });
  return data;
}

export async function uploadDocument(
  file: File,
  params: {
    module_type: string;
    document_subtype: string;
    deal_name?: string;
    analyst_name?: string;
    assessment_id?: string;
    /* Sprint 5 — set when uploading via the project-scoped route. The
       backend resolves the project and links the Assessment row with
       project_id + stage_type + stage_sequence, so the Project
       Workspace sees the assessment as part of its pipeline. */
    project_audit_id?: string;
  }
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<UploadResponse>('/documents/upload', formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function listAssessments(params?: { module_type?: string; limit?: number; offset?: number }): Promise<AssessmentListResponse> {
  const { data } = await api.get<AssessmentListResponse>('/assessments', { params });
  return data;
}

export async function getAssessmentStatus(assessmentId: string): Promise<AssessmentStatusResponse> {
  const { data } = await api.get<AssessmentStatusResponse>(`/assessments/${assessmentId}/status`);
  return data;
}

export async function getAssessmentAnalysis(assessmentId: string): Promise<AnalysisResponse> {
  const { data } = await api.get<AnalysisResponse>(`/assessments/${assessmentId}/analysis`);
  return data;
}

export async function getJobStatus(jobId: string): Promise<JobStatusResponse> {
  const { data } = await api.get<JobStatusResponse>(`/documents/${jobId}/status`);
  return data;
}

export async function cancelJob(jobId: string): Promise<void> {
  await api.delete(`/documents/${jobId}`);
}

export async function getReport(jobId: string): Promise<ReportResponse> {
  const { data } = await api.get<ReportResponse>(`/documents/${jobId}/report`);
  return data;
}

export async function triggerAnalysis(jobId: string): Promise<{ task_id: string; job_id: string; message: string }> {
  const { data } = await api.post(`/documents/${jobId}/analyse`);
  return data;
}

export async function getAnalysis(jobId: string): Promise<AnalysisResponse> {
  const { data } = await api.get<AnalysisResponse>(`/documents/${jobId}/analysis`);
  return data;
}

export async function setInvestmentType(
  assessmentId: string,
  investment_type_key: string,
  investment_type_label: string,
): Promise<{ message: string; assessment_id: string }> {
  const { data } = await api.post(`/assessments/${assessmentId}/investment-type`, {
    investment_type_key,
    investment_type_label,
  });
  return data;
}

export async function cancelAssessment(
  assessmentId: string,
): Promise<{ message: string; assessment_id: string; status: string }> {
  const { data } = await api.post(`/assessments/${assessmentId}/cancel`);
  return data;
}

/* ============================================================
   Sprint 5 — Project lifecycle + Aegis Risk Assessment endpoints
   ============================================================ */

import type {
  AssessmentStarted,
  ClaimListResponse,
  ExternalApiLogResponse,
  ISAOpinionClass,
  OpinionHistory,
  ProjectCreateRequest,
  ProjectDetail,
  ProjectListResponse,
  ProjectOverallStatus,
  ReviewerOverrideRecord,
  RiskAssessmentDetail,
  StageRestartResponse,
} from '../types/api';

export async function createProject(
  payload: ProjectCreateRequest,
): Promise<ProjectDetail> {
  const { data } = await api.post<ProjectDetail>('/projects', payload);
  return data;
}

export async function listProjects(params?: {
  status?: ProjectOverallStatus;
  analyst_id?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<ProjectListResponse> {
  const { data } = await api.get<ProjectListResponse>('/projects', { params });
  return data;
}

export async function getProject(auditId: string): Promise<ProjectDetail> {
  const { data } = await api.get<ProjectDetail>(`/projects/${auditId}`);
  return data;
}

export async function uploadDDToProject(
  auditId: string,
  files: File[],
  options?: { ic_resolution_waived?: boolean; waiver_reason?: string },
): Promise<AssessmentStarted> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  if (options?.ic_resolution_waived) {
    form.append('ic_resolution_waived', 'true');
    if (options.waiver_reason) form.append('waiver_reason', options.waiver_reason);
  }
  const { data } = await api.post<AssessmentStarted>(
    `/projects/${auditId}/dd/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function uploadSPAToProject(
  auditId: string,
  files: File[],
): Promise<AssessmentStarted> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  const { data } = await api.post<AssessmentStarted>(
    `/projects/${auditId}/spa/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function rerunProjectStage(
  auditId: string,
  stage: 'dd' | 'spa' | 'risk',
): Promise<AssessmentStarted> {
  const { data } = await api.post<AssessmentStarted>(
    `/projects/${auditId}/stage/${stage}/rerun`,
  );
  return data;
}

/* Step 2/3 of restart-from-stage rollout (2026-06-28). Restart a
   failed audit stage from the beginning. Server returns 503 when
   the feature flag is off, 409 if a restart is already in flight or
   a downstream stage is busy, 422 if the stage isn't in a restartable
   state. The caller (ProjectWorkspacePage) maps each to a friendly
   alert via the axios interceptor's error.message + a per-code branch. */
export async function restartProjectStage(
  auditId: string,
  stage: 'dd' | 'spa' | 'risk',
): Promise<StageRestartResponse> {
  const { data } = await api.post<StageRestartResponse>(
    `/projects/${auditId}/stage/${stage}/restart`,
  );
  return data;
}

export async function runRiskAssessment(
  auditId: string,
): Promise<{ risk_id: string; status: 'pending' | 'running' }> {
  const { data } = await api.post<{
    risk_id: string;
    status: 'pending' | 'running';
  }>(`/projects/${auditId}/risk/run`);
  return data;
}

export async function getRiskAssessment(
  auditId: string,
): Promise<RiskAssessmentDetail> {
  const { data } = await api.get<RiskAssessmentDetail>(
    `/projects/${auditId}/risk`,
  );
  return data;
}

export async function listRiskClaims(
  auditId: string,
  params?: {
    bucket?: 'company' | 'deal' | 'market';
    status?: 'confirmed' | 'consistent' | 'divergent' | 'not_found';
  },
): Promise<ClaimListResponse> {
  const { data } = await api.get<ClaimListResponse>(
    `/projects/${auditId}/risk/claims`,
    { params },
  );
  return data;
}

export async function getExternalApiLog(
  auditId: string,
): Promise<ExternalApiLogResponse> {
  const { data } = await api.get<ExternalApiLogResponse>(
    `/projects/${auditId}/risk/api-log`,
  );
  return data;
}

/* Sprint 5 — consolidated 17-section payload powering RiskReportPage.
   Backend aggregator combines DD report JSON + SPA report JSON +
   RiskAssessment + claims + API log into one round-trip. */
export interface ConsolidatedReport {
  project: {
    audit_id: string;
    project_name: string;
    target_entity: string;
    investment_type: string | null;
    deal_size_usd_m: number | null;
    sector: string | null;
    jurisdiction: string | null;
    target_close: string | null;
    overall_status: string;
    created_at: string | null;
    /* Sprint 5 client feedback 2026-06-25 (Issues/§1) — lead-analyst
       full name, resolved BE-side from project.lead_analyst_id. */
    analyst_name: string | null;
  };
  dd: {
    assessment_id: string | null;
    status: string;
    report: Record<string, unknown> | null;
  };
  spa: {
    assessment_id: string | null;
    status: string;
    report: Record<string, unknown> | null;
  };
  risk: Record<string, unknown>;
  claims: Array<{
    id: string;
    bucket: string;
    materiality: string;
    field: string;
    claimed_value: string;
    public_value: string | null;
    source_url: string | null;
    source_document: string | null;
    source_page: number | null;
    status: string;
    rationale: string | null;
    confidence: number | null;
  }>;
  api_calls: Array<{
    api: string;
    query_string: string;
    response_status: string;
    latency_ms: number | null;
    estimated_cost_usd: number;
    called_at: string;
  }>;
  api_total_cost_usd: number;
}

export async function getConsolidatedReport(
  auditId: string,
): Promise<ConsolidatedReport> {
  const { data } = await api.get<ConsolidatedReport>(
    `/projects/${auditId}/consolidated-report`,
  );
  return data;
}

/* Sprint 5 client feedback 2026-07-12 — the Draft Report: the same document the
   OIA PDF prints, composed once at the end of Risk processing and saved. Two
   halves:
     generated — AI/derived, read-only. Composed by the SAME backend function the
                 PDF renders from, so the panel and the download cannot drift.
     editable  — analyst-authored. Blank until filled in; the PDF prints whatever
                 is saved here.
   `generated` is null for projects processed before this shipped — the panel
   offers a Generate button rather than showing an empty document. */
export interface DraftFinding {
  source_fid: string;
  title: string;
  rating_label: string;
  rating_class: string;
  /* HTML fragments from the backend renderer. The prose inside is LLM-authored,
     so the panel strips the tags and renders it as text — never innerHTML. */
  observation_html: string;
  risk_impact_html: string;
  root_cause_html: string;
  recommendation_html: string;
  mgmt_action_plan: string;
  responsibility: string;
  target_date_str: string;
}

export interface DraftGenerated {
  executive_summary: string;
  scope_suggestions: string[];
  audit_rating: {
    label: string;
    subtitle: string;
    gauge_position: number;
    css_class: string;
  };
  good_practice: Array<{ title: string; bullets?: string[] }>;
  key_findings: Array<{ title: string; description: string }>;
  root_cause_themes: Array<{ label: string; pct: number; colour?: string }>;
  summary_row: {
    high: number;
    medium: number;
    low: number;
    recommendation: number;
    total: number;
  };
  findings: DraftFinding[];
  generated_at: string;
}

export interface DraftEditable {
  objectives: string;
  scope: string[];
  audit_team: { engagement_manager: string; team_members: string[] };
  findings: Record<
    string,
    { mgmt_action_plan: string; responsibility: string; target_date: string }
  >;
}

export interface DraftReport {
  generated: DraftGenerated | null;
  editable: DraftEditable;
}

export async function getDraftReport(auditId: string): Promise<DraftReport> {
  const { data } = await api.get<DraftReport>(
    `/projects/${auditId}/draft-report`,
  );
  return data;
}

/** Save the analyst-authored half. Never touches `generated`. */
export async function saveDraftReport(
  auditId: string,
  editable: DraftEditable,
): Promise<DraftReport> {
  const { data } = await api.put<DraftReport>(
    `/projects/${auditId}/draft-report`,
    { editable },
  );
  return data;
}

/** Compose the draft now. SLOW — one LLM call per un-augmented finding. Only
 *  needed for projects processed before the draft was generated in-pipeline. */
export async function generateDraftReport(
  auditId: string,
): Promise<DraftReport> {
  const { data } = await api.post<DraftReport>(
    `/projects/${auditId}/draft-report/generate`,
    {},
    { timeout: 15 * 60 * 1000 },
  );
  return data;
}

/* Sprint 5 client feedback 2026-07-11 — OIA-branded Internal Audit
   Report PDF. Rendered server-side via WeasyPrint using the OIA
   sample template (Oman Investment Authority Eagle sample). Every
   §11 Consolidated Finding renders as a §4 page; full-bleed cover,
   wave header/footer, Appendix A + B included. Delivered as PDF —
   there is no DOCX fallback. */
export async function downloadOiaAuditPdf(auditId: string): Promise<void> {
  const response = await api.get(`/projects/${auditId}/oia-audit-pdf`, {
    responseType: 'blob',
  });
  const cd: string | undefined =
    (response.headers as Record<string, string>)['content-disposition'];
  const match = cd ? /filename="?([^";]+)"?/.exec(cd) : null;
  const filename = match ? match[1] : `OIA_Audit_Report_${auditId}.pdf`;
  const blobUrl = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

export async function setReviewerOverride(
  auditId: string,
  payload: { override_class: ISAOpinionClass; override_paragraph: string },
): Promise<ReviewerOverrideRecord> {
  const { data } = await api.patch<ReviewerOverrideRecord>(
    `/projects/${auditId}/risk/opinion-override`,
    payload,
  );
  return data;
}

export async function getOpinionHistory(
  auditId: string,
): Promise<OpinionHistory> {
  const { data } = await api.get<OpinionHistory>(
    `/projects/${auditId}/risk/opinion-history`,
  );
  return data;
}

export default api;
